/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { Button } from '@mui/material';
import { SubTitleLayout, SubTitleGroup } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { ContentFlex } from 'components/layouts/ContentFlex';
import { useTranslation } from 'react-i18next';
import CustomDialog from 'components/modals/common/CustomDialog';
import CustomGrid from 'components/grids/CustomGrid';
import { SearchParam } from 'models/mp/MpPlnt';
import { useMessageBar } from 'hooks/useMessageBar';
import { DrwList } from 'models/mp/MpDrw';
import { getDrwList, getMpDrwList } from 'apis/mp/MpDrw';

/**
 * 제작사양서 조회 팝업
 */

interface Props {
  open: boolean;
  close: (isDone?: boolean) => void;
  condition: any;
  onCallbackData: any;
}

export const MpDrwRetrievePopUp = ({ open, close, condition, onCallbackData }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();

  const gridRef = useRef<any>();
  const [flexRef, setFlexRef] = useState<any>();
  const [rowData, setRowData] = useState<DrwList[]>([]);

  const [searchState, setSearchState] = useState<boolean>(false);

  useEffect(() => {
    if (!_.isEmpty(condition)) {
      setSearchState(true);
    }
  }, [condition]);

  useEffect(() => {
    if (searchState) {
      selectMpDrwList();
    }
  }, [searchState]);

  const selectMpDrwList = async () => {
    await getMpDrwList(condition).then((result) => {
      if (result !== null) {
        setRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
        return false;
      }
    });
  };

  const layoutDefinition = [
    {
      binding: 'drwId',
      visible: false,
    },
    {
      binding: 'drwNo',
      header: String(t('mp.label.관리번호', '관리번호')),
      align: 'center',
      width: 160,
      isReadOnly: true,
    },
    {
      binding: 'drwTpCd',
      header: String(t('mp.label.도면유형코드', '도면유형코드')),
      align: 'center',
      isReadOnly: true,
      visible: false,
    },
    {
      binding: 'drwTpNm',
      header: String(t('mp.label.유형명', '유형명')),
      align: 'center',
      isReadOnly: true,
      width: 120,
    },
    {
      binding: 'drwNm',
      header: String(t('mp.label.자료명', '자료명')),
      align: 'left',
      width: '*',
      minWidth: 150,
      isReadOnly: true,
    },
    {
      header: String(t('mp.label.개정요청', '개정요청')),
      columns: [
        {
          binding: 'chgRsnCtn',
          header: String(t('mp.label.내용', '내용')),
          width: 500,
          align: 'left',
          isReadOnly: true,
        },
        {
          binding: 'optValCtn2',
          header: String(t('mp.label.상태', '상태')),
          width: 80,
          align: 'center',
          isReadOnly: true,
        },
        {
          binding: 'optValCtn3',
          header: String(t('mp.label.결재결과', '결재결과')),
          width: 90,
          align: 'center',
          isReadOnly: true,
        },
        {
          binding: 'optValCtn4',
          header: String(t('mp.label.처리내용', '처리내용')),
          width: 111,
          align: 'left',
          isReadOnly: true,
        },
      ],
    },
    {
      binding: 'dataInsUserNm',
      header: String(t('mp.label.요청자', '요청자')),
      align: 'center',
      width: 100,
      isReadOnly: true,
    },
    {
      binding: 'dataInsDtm',
      header: String(t('mp.label.요청일자', '요청일자')),
      align: 'center',
      width: 100,
      isReadOnly: true,
    },
    {
      binding: 'dataUpdDtm',
      header: String(t('mp.label.수정일자', '수정일자')),
      align: 'center',
      width: 100,
      isReadOnly: true,
    },
  ];

  const onInitialized = (grid) => {
    setFlexRef(grid);
    gridRef.current = grid;
  };

  const handleClose = () => {
    close();
  };

  return (
    <>
      <CustomDialog
        open={open}
        title={t('mp.label.개정대상 제작사양서 조회', '개정대상 제작사양서 조회')}
        size="lg"
        onClose={handleClose}
      >
        <SubTitleLayout>
          <SubTitleGroup>
            <h3>{t('mp.label.제작사양서 List', '제작사양서 List')}</h3>
            <span className="total">
              {t('com.label.총', '총')}
              <span>{(rowData || []).length.toLocaleString()}</span>
              {t('com.label.건', '건')}
            </span>
          </SubTitleGroup>
        </SubTitleLayout>
        <CustomGrid
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          allowPinning={false}
          isSelector={false}
          isFilter={false}
          height={300}
          align={'center'}
          initialized={onInitialized}
        />
      </CustomDialog>
    </>
  );
};
